import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Snackbar,
  Tooltip,
  DialogTitle
} from '@mui/material';
import { NavigateBefore as BackIcon, Edit as EditIcon, InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ContextSession } from "../../services/ContextSession";

const EditarDemanda = () => {
  const navigate = useNavigate();
  const sessi = useContext(ContextSession);
  const [procuradorData, setProcuradorData] = useState({});
  const [loading, setLoading] = useState({
    procurador: false,
    submitting: false
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [procuradorOriginal, setProcuradorOriginal] = useState(null);
  const [errores, setErrores] = useState({});
  const [apiConfig, setApiConfig] = useState({
    fields: [],
    rules: [],
    catalogs: {}
  });

  // Tooltips personalizados para cada campo
  const fieldTooltips = {
    accioninstitucion: "Descripción de la acción tomada por la institución respecto a la demanda. Incluya detalles sobre las medidas, estrategias o pasos seguidos en el proceso legal.",
    resultadoprimerainstancia: "Resultado obtenido en la primera instancia del proceso judicial. Si selecciona 'No aplica', la segunda instancia se bloqueará automáticamente.",
    resultadosegundainstancia: "Resultado obtenido en la segunda instancia del proceso judicial. Este campo se bloquea automáticamente si la primera instancia es 'No aplica'.",
    fechaactuacion: "Fecha del último resultado de instancia. Obligatorio cuando hay resultados en primera o segunda instancia. Debe ser posterior a la fecha de presentación de la demanda. Si ambos resultados son 'No aplica', este campo debe permanecer vacío.",
    montogastoslegales: "Valor de erogación por gastos legales u honorarios profesionales. En caso de demandas gestionadas por empleados, se debe asignar el gasto mediante prorrateo. Si no existen gastos, ingrese 0.00.",
    cuentagastos: "Ingrese una cuenta válida del catálogo contable vigente que pertenezca al pasivo o a gastos"
  };

  // Obtener datos de la demanda del contexto y cargar configuración de procurador
  useEffect(() => {
    const demandaData = sessi.getVariable("demandaData");
    const demandaID = sessi.getVariable("demandaID");

    if (!demandaData || !demandaID) {
      navigate('/Legal/TipoCaso');
      return;
    }

    // Cargar configuración de la API de procurador
    cargarConfiguracionProcurador(demandaData);
    
  }, [navigate, sessi]);

  // Cargar configuración de la API de procurador
  const cargarConfiguracionProcurador = async (demandaData) => {
    try {
      setLoading(prev => ({ ...prev, procurador: true }));
      const token = sessi.sessionData.token;
      
      const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramites/campos/procurador/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) throw new Error('Error al cargar configuración de procurador');

      const data = await response.json();
      
      // Ordenar el catálogo por id
      if (data.catalogs?.cat_resultado_instancia) {
        data.catalogs.cat_resultado_instancia = data.catalogs.cat_resultado_instancia.sort((a, b) => a.id - b.id);
      }

      setApiConfig(data);
      
      // Inicializar datos del procurador con valores de la demanda
      const datosInicialesProcurador = {
        accioninstitucion: demandaData.accioninstitucion || '',
        resultadoprimerainstancia: demandaData.resultadoprimerainstancia || 3, // No aplica
        resultadosegundainstancia: demandaData.resultadosegundainstancia || 3, // No aplica
        fechaactuacion: demandaData.fechaactuacion ? 
        demandaData.fechaactuacion.split('T')[0] : '',
        montogastoslegales: demandaData.montogastoslegales || '0.00',
        cuentagastos: demandaData.cuentagastos,
        // Campos de solo lectura
        usuarioprocuradorid: demandaData.usuarioprocuradorid || '',
        procuradorasignado: demandaData.procuradorasignado || '',
        fechaasignacion: demandaData.fechaasignacion || ''
      };

      setProcuradorData(datosInicialesProcurador);
      setProcuradorOriginal({ ...datosInicialesProcurador });
      
    } catch (err) {
      console.error('Error al cargar configuración de procurador:', err);
      setError('Error al cargar configuración del formulario');
    } finally {
      setLoading(prev => ({ ...prev, procurador: false }));
    }
  };

  // Efecto para manejar la lógica de dependencia entre instancias
  useEffect(() => {
    if (procuradorData.resultadoprimerainstancia === 3) {
      setProcuradorData(prev => ({
        ...prev,
        resultadosegundainstancia: 3,
        fechaactuacion: '' // Limpiar fecha si no aplica
      }));
    }
  }, [procuradorData.resultadoprimerainstancia]);

  // Efecto para manejar la lógica de validación entre monto y cuenta contable
  useEffect(() => {
    // Cuando el monto cambia a 0.00, limpiar la cuenta contable
    const monto = parseFloat(procuradorData.montogastoslegales) || 0;
    if (monto === 0 && procuradorData.cuentagastos) {
      setProcuradorData(prev => ({
        ...prev,
        cuentagastos: ''
      }));
    }
    
    // Limpiar error de cuenta cuando se modifica el monto
    if (errores.cuentagastos && monto === 0) {
      setErrores(prev => ({
        ...prev,
        cuentagastos: ''
      }));
    }
  }, [procuradorData.montogastoslegales]);

  const handleProcuradorChange = (field) => (event) => {
    const value = event.target.value;
    
    setProcuradorData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error del campo cuando se modifica
    if (errores[field]) {
      setErrores(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // FUNCIÓN PARA VALIDAR LA RELACIÓN MONTO - CUENTA CONTABLE (NUEVA VALIDACIÓN)
  const validarRelacionMontoCuenta = () => {
    const monto = parseFloat(procuradorData.montogastoslegales) || 0;
    const cuenta = procuradorData.cuentagastos;
    
    // Caso 1: Monto > 0.00 → Debe tener cuenta contable válida
    if (monto > 0) {
      if (!cuenta || cuenta.trim() === '') {
        return {
          error: true,
          mensajeMonto: null,
          mensajeCuenta: 'Ingrese una cuenta válida del catálogo contable vigente que pertenezca al pasivo o a gastos'
        };
      }
    }
    // Caso 2: Monto = 0.00 → Cuenta debe estar vacía
    else if (monto === 0) {
      if (cuenta && cuenta.trim() !== '') {
        return {
          error: true,
          mensajeMonto: null,
          mensajeCuenta: 'La cuenta contable debe permanecer vacía cuando el monto es 0.00'
        };
      }
    }
    
    return { error: false, mensajeMonto: null, mensajeCuenta: null };
  };

  // Función para validar el formulario (MODIFICADA)
  const validarFormulario = () => {
    const nuevosErrores = {};
    const demandaData = sessi.getVariable("demandaData");

    // Validar Acción de la Institución
    if (!procuradorData.accioninstitucion || procuradorData.accioninstitucion.trim() === '') {
      nuevosErrores.accioninstitucion = 'La acción de la institución es obligatoria';
    } else if (procuradorData.accioninstitucion.length > 500) {
      nuevosErrores.accioninstitucion = 'Máximo 500 caracteres';
    }

    // Validar Monto Gastos Legales (OBLIGATORIO - no puede venir vacío)
    if (procuradorData.montogastoslegales === '' || procuradorData.montogastoslegales === null || procuradorData.montogastoslegales === undefined) {
      nuevosErrores.montogastoslegales = 'El monto de gastos legales es obligatorio. En caso de no existir gastos, llenar con 0.00';
    } else {
      const monto = parseFloat(procuradorData.montogastoslegales);
      if (isNaN(monto)) {
        nuevosErrores.montogastoslegales = 'El monto debe ser un número válido';
      } else if (monto < 0) {
        nuevosErrores.montogastoslegales = 'El monto debe ser mayor o igual a 0';
      }
    }

    // VALIDACIÓN ESPECÍFICA: Relación entre Monto y Cuenta Contable
    const validacionMontoCuenta = validarRelacionMontoCuenta();
    if (validacionMontoCuenta.error) {
      if (validacionMontoCuenta.mensajeMonto) {
        nuevosErrores.montogastoslegales = validacionMontoCuenta.mensajeMonto;
      }
      if (validacionMontoCuenta.mensajeCuenta) {
        nuevosErrores.cuentagastos = validacionMontoCuenta.mensajeCuenta;
      }
    }

    // VALIDACIÓN ESPECÍFICA SOLICITADA: Fecha de Actuación
    const tieneResultadoValido = 
      procuradorData.resultadoprimerainstancia !== 3 || 
      procuradorData.resultadosegundainstancia !== 3;

    if (tieneResultadoValido) {
      // CASO 1: Si hay resultados en instancias → Fecha es OBLIGATORIA y debe ser POSTERIOR a Presentación
      if (!procuradorData.fechaactuacion) {
        nuevosErrores.fechaactuacion = 'La fecha de actuación es obligatoria cuando hay resultados en instancias';
      } else {
        // Validar que sea posterior a la fecha de presentación
        const fechaActuacion = new Date(procuradorData.fechaactuacion);
        const fechaPresentacion = demandaData.fechapresentacion ? 
          new Date(demandaData.fechapresentacion.split('T')[0]) : null;
        
        if (fechaPresentacion && fechaActuacion <= fechaPresentacion) {
          nuevosErrores.fechaactuacion = 'La fecha de actuación debe ser posterior a la fecha de presentación de la demanda';
        }
      }
    } else {
      // CASO 2: Si ambos resultados son "No aplica" → Fecha debe estar VACÍA
      if (procuradorData.fechaactuacion) {
        nuevosErrores.fechaactuacion = 'La fecha de actuación debe estar vacía cuando no hay resultados en instancias';
      }
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Función para preparar los datos para la API de procurador
  const prepararDatosProcuradorParaAPI = () => {
    const monto = parseFloat(procuradorData.montogastoslegales) || 0;
    const cuenta = monto === 0 ? '' : procuradorData.cuentagastos;

    const datosParaEnviar = {
      accioninstitucion: procuradorData.accioninstitucion,
      resultadoprimerainstancia: parseInt(procuradorData.resultadoprimerainstancia),
      resultadosegundainstancia: parseInt(procuradorData.resultadosegundainstancia),
      fechaactuacion: procuradorData.fechaactuacion || null,
      montogastoslegales: monto,
      cuentagastos: cuenta,
    };

    console.log('Datos antes de limpiar:', datosParaEnviar);
    return datosParaEnviar;
  };

  // Función para actualizar datos del procurador
  const actualizarDatosProcurador = async () => {
    try {
      const token = sessi.sessionData.token;
      const demandaID = sessi.getVariable("demandaID");

      const datosProcurador = prepararDatosProcuradorParaAPI();
      
      console.log('🔵 ENVIANDO PATCH:', {
        url: `https://bei360.injupemp.gob.hn:8444/api/tramite/actualizar-procurador/${demandaID}/`,
        datos: datosProcurador,
        token: token ? 'Token presente' : 'Token ausente'
      });

      const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramite/actualizar-procurador/${demandaID}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(datosProcurador)
      });

      console.log('RESPONSE STATUS:', response.status);
      console.log('RESPONSE OK:', response.ok);

      if (!response.ok) {
        let errorMessage = `Error ${response.status}`;
        try {
          const errorData = await response.json();
          console.log(' ERROR RESPONSE DATA:', errorData);
          errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
        } catch (parseError) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log(' RESPUESTA EXITOSA:', data);
      return data;
      
    } catch (err) {
      console.error(' ERROR COMPLETO:', err);
      throw err;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    console.log('INICIANDO SUBMIT...');
    
    // Validar formulario antes de enviar
    const esValido = validarFormulario();
    console.log('Formulario válido:', esValido);
    
    if (!esValido) {
      console.log('Validación falló');
      setError('Por favor, corrija los errores en el formulario');
      return;
    }

    setLoading(prev => ({ ...prev, submitting: true }));
    setError(null);
    setSuccess(false);

    try {
      // Verificar cambios
      const hayCambios = hayCambiosProcuradorSinGuardar();
      console.log('📋 ¿Proceder con PATCH?', hayCambios);
      
      if (hayCambios) {
        console.log('🔄 Ejecutando actualizarDatosProcurador...');
        const resultado = await actualizarDatosProcurador();
        console.log('✅ Actualización completada:', resultado);
        
        setSuccess(true);
        
        // Actualizar el estado original con los nuevos datos
        setProcuradorOriginal(procuradorData);
        
        setTimeout(() => {
          navigate('/Legal/TipoCaso');
        }, 2000);
      } else {
        console.log('ℹ No hay cambios detectados');
        setError('No hay cambios para guardar');
      }

    } catch (err) {
      console.error(' ERROR EN SUBMIT:', err);
      
      // Manejo específico de errores
      if (err.message.includes('cuenta válida del catálogo contable')) {
        setError('La cuenta asignada para gastos legales no es válida.');
      } else if (err.message.includes('401')) {
        setError('Error de autenticación. Por favor, inicie sesión nuevamente.');
      } else if (err.message.includes('403')) {
        setError('No tiene permisos para realizar esta acción.');
      } else if (err.message.includes('404')) {
        setError('No se encontró la demanda especificada.');
      } else if (err.message.includes('400')) {
        setError('Error en los datos enviados. Verifique la información.');
      } else if (err.message.includes('500')) {
        setError('Error interno del servidor. Contacte al administrador.');
      } else {
        setError(err.message || 'Error desconocido al actualizar los datos.');
      }
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleBack = () => {
    navigate('/Legal/TipoCaso');
  };

  const handleCloseNotification = () => {
    setError(null);
    setSuccess(false);
  };

  const getNaturalezaText = (naturaleza) => {
    switch (parseInt(naturaleza)) {
      case 1: return 'A Favor';
      case 2: return 'En Contra';
      default: return 'Desconocido';
    }
  };

  const getResultadoInstanciaText = (resultado) => {
    switch (parseInt(resultado)) {
      case 1: return 'Favorable';
      case 2: return 'Desfavorable';
      case 3: return 'No aplica';
      default: return 'No especificado';
    }
  };

  // Verificar si hay cambios sin guardar en los datos del procurador
  const hayCambiosProcuradorSinGuardar = () => {
    if (!procuradorOriginal) {
      console.log('⚠️ procuradorOriginal no está definido');
      return true; // Si no hay original, asumimos que hay cambios
    }
    
    const camposComparar = [
      'accioninstitucion', 
      'resultadoprimerainstancia',
      'resultadosegundainstancia', 
      'fechaactuacion', 
      'montogastoslegales',
      'cuentagastos'
    ];

    let hayCambios = false;

    camposComparar.forEach(campo => {
      const valorOriginal = procuradorOriginal[campo];
      const valorActual = procuradorData[campo];
      const cambio = valorOriginal !== valorActual;
      
      if (cambio) {
        console.log(`🔄 CAMBIO DETECTADO en ${campo}:`, {
          original: valorOriginal,
          actual: valorActual
        });
        hayCambios = true;
      }
    });

    console.log('📊 ¿Hay cambios sin guardar?', hayCambios);
    return hayCambios;
  };

  // Componente para el ícono de información con Tooltip
  const InfoIconWithTooltip = ({ fieldName }) => (
    <Tooltip title={fieldTooltips[fieldName] || `Información sobre ${fieldName}`} arrow placement="top">
      <InfoOutlined 
        color="action" 
        sx={{ 
          fontSize: 18, 
          ml: 1,
          cursor: 'help',
          verticalAlign: 'middle'
        }} 
      />
    </Tooltip>
  );

  // Renderizar campo según su configuración
  const renderField = (field) => {
    const value = procuradorData[field.name] || '';
    const error = errores[field.name];
    const isReadOnly = field.ui?.widget === 'readonly' || field.ui?.readonly;
    const isEditable = !isReadOnly && field.ui?.widget !== 'readonly';

    const commonProps = {
      fullWidth: true,
      label: (
        <Box component="span" display="flex" alignItems="center">
          {field.ui?.label || field.name}
          {isEditable && <InfoIconWithTooltip fieldName={field.name} />}
        </Box>
      ),
      value: value,
      onChange: handleProcuradorChange(field.name),
      error: !!error,
      helperText: error || getHelperText(field),
      disabled: isReadOnly || (field.name === 'resultadosegundainstancia' && procuradorData.resultadoprimerainstancia === 3),
      margin: "normal"
    };

    switch (field.type) {
      case 'fk':
        const options = apiConfig.catalogs[field.catalog] || [];
        return (
          <FormControl 
            fullWidth 
            error={!!error}
            disabled={isReadOnly || (field.name === 'resultadosegundainstancia' && procuradorData.resultadoprimerainstancia === 3)}
            margin="normal"
          >
            <InputLabel>
              <Box component="span" display="flex" alignItems="center">
                {field.ui?.label || field.name}
                {isEditable && <InfoIconWithTooltip fieldName={field.name} />}
              </Box>
            </InputLabel>
            <Select
              value={value}
              onChange={handleProcuradorChange(field.name)}
              label={field.ui?.label || field.name}
            >
              <MenuItem value=""><em>Seleccione...</em></MenuItem>
              {options.map(option => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
            {!error && field.name === 'resultadosegundainstancia' && procuradorData.resultadoprimerainstancia === 3 && (
              <Typography variant="caption" color="text.secondary">
                Bloqueado porque Primera Instancia es "No aplica"
              </Typography>
            )}
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            {...commonProps}
            type="date"
            InputLabelProps={{ shrink: true }}
            disabled={commonProps.disabled || (procuradorData.resultadoprimerainstancia === 3 && procuradorData.resultadosegundainstancia === 3)}
          />
        );

      case 'datetime':
        return (
          <TextField
            {...commonProps}
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'decimal':
        return (
          <TextField
            {...commonProps}
            type="number"
            inputProps={{
              step: field.ui?.step || "0.01",
              min: field.min || 0
            }}
          />
        );

      case 'string':
      default:
        // Para el campo cuentagastos, agregar validación especial
        if (field.name === 'cuentagastos') {
          const monto = parseFloat(procuradorData.montogastoslegales) || 0;
          return (
            <TextField
              {...commonProps}
              type="text"
              disabled={monto === 0} // Deshabilitar cuando el monto es 0
              InputProps={{
                readOnly: monto === 0,
                style: {
                  backgroundColor: monto === 0 ? '#f5f5f5' : 'inherit'
                }
              }}
              placeholder={monto === 0 ? 'No aplica cuando el monto es 0.00' : 'Ingrese cuenta contable'}
            />
          );
        }
        return (
          <TextField
            {...commonProps}
            type="text"
            multiline={field.name === 'accioninstitucion'}
            rows={field.name === 'accioninstitucion' ? 4 : 1}
            inputProps={{
              maxLength: field.maxLength
            }}
          />
        );
    }
  };

  // Función para obtener texto de ayuda específico para cada campo
  const getHelperText = (field) => {
    const monto = parseFloat(procuradorData.montogastoslegales) || 0;
    
    switch (field.name) {
      case 'accioninstitucion':
        return 'Descripción detallada de la acción tomada por la institución (máximo 500 caracteres)';
      case 'resultadoprimerainstancia':
        return 'Seleccione el resultado obtenido en primera instancia';
      case 'resultadosegundainstancia':
        return procuradorData.resultadoprimerainstancia === 3 
          ? 'Bloqueado porque Primera Instancia es "No aplica"'
          : 'Seleccione el resultado obtenido en segunda instancia';
      case 'fechaactuacion':
        const tieneResultadoValido = 
          procuradorData.resultadoprimerainstancia !== 3 || 
          procuradorData.resultadosegundainstancia !== 3;
        
        if (tieneResultadoValido) {
          return 'Fecha del último resultado de instancia (obligatoria y debe ser posterior a la fecha de presentación)';
        } else {
          return 'Este campo debe permanecer vacío cuando no hay resultados en instancias';
        }
      case 'montogastoslegales':
        return 'En caso de no existir gastos legales, llenar con 0.00';
      case 'cuentagastos':
        if (monto > 0) {
          return 'Ingrese una cuenta válida del catálogo contable vigente que pertenezca al pasivo o a gastos';
        } else if (monto === 0) {
          return 'Este campo debe permanecer vacío cuando el monto es 0.00';
        } else {
          return 'Ingrese una cuenta válida del catálogo contable';
        }
      default:
        return ' ';
    }
  };

  const demandaData = sessi.getVariable("demandaData");
  const demandaID = sessi.getVariable("demandaID");

  if (!demandaData || !demandaID) {
    return (
      <Container style={{ marginTop: 15 }}>
        <Alert severity="error">
          No se encontraron datos de la demanda. Redirigiendo...
        </Alert>
      </Container>
    );
  }

  if (loading.procurador) {
    return (
      <Container style={{ marginTop: 15, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container style={{ marginTop: 15 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, borderRadius: 2 }}>
        {hayCambiosProcuradorSinGuardar() && (
          <Chip 
            label="Cambios sin guardar" 
            color="warning" 
            size="small" 
            sx={{ ml: 2 }}
          />
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
       <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} />
            Completar Campos Demanda #{demandaID}
          </Box>
        </DialogTitle>

        {/* Información básica */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Información de la Demanda
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Número de Expediente:
              </Typography>
              <Typography variant="body1">
                {demandaData.numeroexpediente || 'No asignado'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Naturaleza:
              </Typography>
              <Chip 
                label={getNaturalezaText(demandaData.naturaleza)} 
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Estado:
              </Typography>
              <Chip 
                label={demandaData.estado || 'N/A'} 
                color={
                  demandaData.estado === 'Activo' ? 'primary' :
                  demandaData.estado === 'Cerrado' ? 'success' :
                  demandaData.estado === 'Vencido' ? 'error' : 'default'
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Fecha de Presentación:
              </Typography>
              <Typography variant="body1">
                {demandaData.fechapresentacion ? 
                  new Date(demandaData.fechapresentacion).toLocaleDateString() : 'No asignada'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Datos del procurador actualizados correctamente. Redirigiendo...
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Campos editables del procurador */}
            {apiConfig.fields
              .filter(field => !field.ui?.readonly && field.ui?.widget !== 'readonly')
              .map((field) => (
                <Grid item xs={12} sm={field.name === 'accioninstitucion' ? 12 : 6} key={field.name}>
                  {renderField(field)}
                </Grid>
              ))}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleBack}
              disabled={loading.submitting}
              color="error"
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={loading.submitting || !hayCambiosProcuradorSinGuardar()}
              sx={{
                backgroundColor: '#19438C',
                '&:hover': {
                  backgroundColor: '#5ab4c0',
                },
                '&:disabled': {
                  backgroundColor: '#cccccc',
                }
              }}
            >
              {loading.submitting ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={handleCloseNotification}>
          Datos del procurador actualizados correctamente. Redirigiendo...
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditarDemanda;